import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './QRCodeLoginPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const QRCodeLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [scannedData, setScannedData] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Handle keyboard input from USB QR scanner
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!isScanning || showSuccessMessage) return;

      // USB QR scanners typically send Enter after the data
      if (event.key === 'Enter') {
        event.preventDefault();
        if (scannedData.trim()) {
          processQRCode(scannedData.trim());
          setScannedData('');
        }
      } else if (event.key.length === 1) {
        // Accumulate characters (excluding special keys)
        setScannedData(prev => prev + event.key);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [isScanning, scannedData, showSuccessMessage]);

  // Focus the input when scanning starts
  useEffect(() => {
    if (isScanning && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isScanning]);

  const startScanning = () => {
    setIsScanning(true);
    setMessage('');
    setScannedData('');
  };

  const stopScanning = () => {
    setIsScanning(false);
    setScannedData('');
  };

  const processQRCode = async (qrData: string) => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setMessage('Processing...');
    
    try {
      // Extract 5-digit PIN from QR code
      const pin = qrData.trim();
      
      if (!/^\d{5}$/.test(pin)) {
        setMessage('Invalid QR code. Please scan a valid 5-digit PIN QR code.');
        setTimeout(() => {
          setMessage('');
          setIsProcessing(false);
        }, 3000);
        return;
      }

      // Check if user is already checked in
      const checkInStatusResponse = await fetch(`${API_BASE_URL}/checkin-status/${pin}`);
      const checkInStatus = await checkInStatusResponse.json();

      if (checkInStatus.is_checked_in) {
        // User is checked in, so check them out
        await checkOutUser(pin, checkInStatus.userName || 'User');
      } else {
        // User is not checked in, so check them in
        await checkInUser(pin);
      }
    } catch (error) {
      setMessage('Error processing QR code. Please try again.');
      setTimeout(() => {
        setMessage('');
        setIsProcessing(false);
      }, 3000);
    }
  };

  const checkInUser = async (pin: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          passcode: pin,
          task_id: 21, // General Maintenance - default task for QR code check-ins
          work_location: 'onsite', // Default work location for QR code check-ins
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage(`Welcome, ${result.userName || 'User'}!`);
        setShowSuccessMessage(true);
        setMessage(''); // Clear any existing message
        setScannedData(''); // Clear any accumulated scan data
        setIsProcessing(false); // Clear processing state immediately
        setTimeout(() => {
          setShowSuccessMessage(false);
          setSuccessMessage('');
          // Continue scanning after 5 seconds
        }, 5000);
      } else {
        setMessage(result.message || 'Check-in failed. Please try again.');
        setTimeout(() => {
          setMessage('');
          setIsProcessing(false);
        }, 3000);
      }
    } catch (error) {
      setMessage('Check-in failed. Please try again.');
      setTimeout(() => {
        setMessage('');
        setIsProcessing(false);
      }, 3000);
    }
  };

  const checkOutUser = async (pin: string, userName: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: pin,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccessMessage(`Goodbye, ${result.userName || userName}!`);
        setShowSuccessMessage(true);
        setMessage(''); // Clear any existing message
        setScannedData(''); // Clear any accumulated scan data
        setIsProcessing(false); // Clear processing state immediately
        setTimeout(() => {
          setShowSuccessMessage(false);
          setSuccessMessage('');
          // Continue scanning after 5 seconds
        }, 5000);
      } else {
        setMessage(result.message || 'Check-out failed. Please try again.');
        setTimeout(() => {
          setMessage('');
          setIsProcessing(false);
        }, 3000);
      }
    } catch (error) {
      setMessage('Check-out failed. Please try again.');
      setTimeout(() => {
        setMessage('');
        setIsProcessing(false);
      }, 3000);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="qr-login-container">
      <div className="qr-login-card">
        <div className="qr-login-header">
          <div className="logo">
            <img src="/VolunteerHeart.png" alt="FabMo Volunteer" className="logo-image" />
          </div>
          <h1 className="qr-login-title">QR Code Login</h1>
          <p className="qr-login-subtitle">
            Scan your QR code to check in or out
          </p>
        </div>

        <div className="qr-login-content">
          {!isScanning ? (
            <div className="qr-scanner-setup">
              <div className="scanner-icon">📱</div>
              <h2>Ready to Scan</h2>
              <p>Click the button below to start scanning QR codes with your USB scanner</p>
              <button 
                className="start-scan-button"
                onClick={startScanning}
                disabled={isProcessing}
              >
                Start Scanning
              </button>
            </div>
          ) : showSuccessMessage ? (
            <div className="qr-scanner-active success-mode">
              <div className="success-content">
                <div className="success-icon">✓</div>
                <h2 className="success-title">{successMessage}</h2>
                <p className="success-subtitle">
                  {successMessage.includes('Welcome') ? 'You are now checked in' : 'You are now checked out'}
                </p>
                <div className="success-timer">
                  <div className="timer-circle">
                    <div className="timer-fill"></div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="qr-scanner-active">
              <div className="scanner-container">
                <div className="scanner-icon-large">📱</div>
                <h2>Scanning Mode Active</h2>
                <p className="scan-instructions">
                  Use your USB QR code scanner to scan a 5-digit PIN QR code
                </p>
                <div className="scanner-input-container">
                  <input
                    ref={inputRef}
                    type="text"
                    value={scannedData}
                    onChange={(e) => setScannedData(e.target.value)}
                    placeholder="QR code data will appear here..."
                    className="scanner-input"
                    readOnly
                  />
                </div>
                <div className="scanner-status">
                  <div className="status-indicator active"></div>
                  <span>Ready to scan</span>
                </div>
              </div>
              <button 
                className="stop-scan-button"
                onClick={stopScanning}
                disabled={isProcessing}
              >
                Stop Scanning
              </button>
            </div>
          )}

          {message && !showSuccessMessage && !isProcessing && (
            <div className="message success">
              {message}
            </div>
          )}
        </div>

        <div className="qr-login-footer">
          <button onClick={handleBack} className="back-button">
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeLoginPage;
